import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileContract,
  faSearch,
  faSpinner,
  faCalendarAlt,
  faTrash,
  faCheckSquare,
  faSquare,
  faFileSignature,
  faMoneyBillWave,
  faInfoCircle,
  faExclamationTriangle,
  faHeadset
} from "@fortawesome/free-solid-svg-icons";

import fileApi from '../../services/api/fileApi';
import useAppSession from '../../stores/useAppSession';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 20px 12px;
  background-color: #f2f2f2;
  min-height: 100vh;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 40px 24px;
  }
`;

const MainCard = styled.div`
  width: 100%;
  max-width: 1100px;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 20px;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 32px;
  }
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterCard = styled.div`
  background: #fafafa;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 32px;

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

const FilterGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 100%; 

  @media (min-width: 600px) {
    flex: 1 1 calc(50% - 16px); 
  }

  @media (min-width: 1024px) {
    flex: 1; 
  }

  label {
    font-size: 13px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
  }
`;

const StyledInput = styled.input`
  height: 40px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #dcdcdc;
  font-size: 15px;
  outline: none;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s;
  &:focus { border-color: #ff8f40; }
`;

const ActionButton = styled.button`
  height: 40px;
  padding: 0 20px;
  background-color: ${props => props.variant === 'danger' ? "#ff3b30" : "#333"};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  width: 100%; 

  @media (min-width: 600px) {
    width: auto;
  }

  &:hover { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const ContractContainer = styled.div`
  background: #ffffff;
  border: 1px solid ${props => props.selected ? "#ff8f40" : "#e1e1e1"};
  border-left: 4px solid ${props => props.selected ? "#ff8f40" : "#333"};
  border-radius: 6px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
  transition: all 0.2s;
  display: flex;
  align-items: flex-start;
  padding: 16px;
`;

const CheckboxArea = styled.div`
  cursor: pointer;
  color: ${props => props.selected ? "#ff8f40" : "#ccc"};
  font-size: 20px;
  padding-right: 16px;
  padding-top: 4px;
  transition: color 0.2s;
  &:hover { color: #ff8f40; }
`;

const ContractBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .contract-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    
    .name {
      font-size: 16px;
      font-weight: 700;
      color: #1d1d1f;
    }
    .company {
      font-size: 13px;
      font-weight: 600;
      color: #ff8f40;
    }
  }

  .details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
    background: #f9f9f9;
    padding: 16px;
    border-radius: 6px;
    border: 1px solid #f0f0f0;
  }

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 4px;

    &.full-width {
      grid-column: 1 / -1;
      border-top: 1px solid #eee;
      padding-top: 8px;
    }

    .label {
      font-size: 10px;
      font-weight: 700;
      color: #999;
      text-transform: uppercase;
      display: flex;
      align-items: center;
      gap: 5px;
      svg { font-size: 10px; }
    }

    .value {
      font-size: 13px;
      font-weight: 600;
      color: #333;
      word-break: break-word;
    }
  }

  .summary {
    font-size: 13px;
    color: #555;
    line-height: 1.5;
    border-top: 1px inset #eee;
    padding-top: 12px;
    font-style: italic;
  }
`;

const DeleteBtn = styled.button`
  background: transparent;
  border: none;
  color: #ccc;
  cursor: pointer;
  font-size: 16px;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s;
  &:hover {
    color: #ff3b30;
    background: rgba(255, 59, 48, 0.05);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px;
  color: #999;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  svg { font-size: 40px; opacity: 0.3; }
`;

const ContractsViewer = () => {
  const { clientId } = useAppSession();
  const [contracts, setContracts] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchContracts = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const response = await fileApi.contrato_getFiles(clientId);
      if (response && response.status === 200) {
        setContracts(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      toast.error("Erro ao carregar contratos.");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const handleToggleSelect = (fileId) => {
    setSelectedFiles(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleDeleteIndividual = async (fileId, fileName) => {
    if (!window.confirm(`Deseja excluir o contrato ${fileName}?`)) return;

    setActionLoading(true);
    try {
      await fileApi.contrato_deleteFile({ clientId }, fileName);
      toast.success("Contrato excluído.");
      setContracts(prev => prev.filter(f => f.file_id !== fileId));
      setSelectedFiles(prev => prev.filter(id => id !== fileId));
    } catch (error) {
      toast.error("Erro ao excluir arquivo.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Deseja excluir os ${selectedFiles.length} contratos selecionados?`)) return;

    setActionLoading(true);
    try {
      await Promise.all(selectedFiles.map(fileId => fileApi.contrato_deleteFile({ clientId }, fileId)));
      toast.success("Exclusão concluída.");
      fetchContracts();
      setSelectedFiles([]);
    } catch (error) {
      toast.error("Erro na exclusão em massa.");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredContracts = contracts.filter(c => {
    const term = searchText.toLowerCase();
    const fileName = (c.file_name || "").toLowerCase();
    const company = (c.metadata?.nome_contratada || "").toLowerCase();
    return fileName.includes(term) || company.includes(term);
  });

  const formatCurrency = (value) => {
    if (!value || value === '-') return '-';
    const num = parseFloat(String(value).replace(',', '.'));
    return isNaN(num) ? value : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '-' || dateStr.length < 5) return '-';
    return dayjs(dateStr).format('DD/MM/YYYY');
  };

  return (
    <PageWrapper>
      <MainCard>
        <Title>
          <FontAwesomeIcon icon={faFileSignature} style={{ color: '#ff8f40' }} />
          Gestão de Contratos
        </Title>

        <FilterCard>
          <FilterGrid>
            <FormGroup style={{ flex: 2 }}>
              <label><FontAwesomeIcon icon={faSearch} /> Pesquisar Contratos</label>
              <StyledInput
                placeholder="Filtrar por nome ou empresa..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </FormGroup>

            {selectedFiles.length > 0 && (
              <ActionButton
                variant="danger"
                onClick={handleBulkDelete}
                disabled={actionLoading}
              >
                <FontAwesomeIcon icon={actionLoading ? faSpinner : faTrash} spin={actionLoading} />
                Excluir Selecionados ({selectedFiles.length})
              </ActionButton>
            )}
          </FilterGrid>
        </FilterCard>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#ff8f40" />
            <p style={{ marginTop: '16px', color: '#666' }}>Carregando base de contratos...</p>
          </div>
        ) : (
          <div>
            {filteredContracts.length === 0 ? (
              <EmptyState>
                <FontAwesomeIcon icon={faFileContract} />
                <p>Nenhum contrato encontrado.</p>
              </EmptyState>
            ) : (
              filteredContracts.map((item) => {
                const isSelected = selectedFiles.includes(item.file_id);
                const metadata = item.metadata || {};

                return (
                  <ContractContainer key={item.file_id} selected={isSelected}>
                    <CheckboxArea onClick={() => handleToggleSelect(item.file_id)} selected={isSelected}>
                      <FontAwesomeIcon icon={isSelected ? faCheckSquare : faSquare} />
                    </CheckboxArea>

                    <ContractBody>
                      <div className="header-row">
                        <div className="contract-info">
                          <span className="name">{item.file_name || 'Sem nome'}</span>
                          {metadata.nome_contratada && metadata.nome_contratada !== '-' && (
                            <span className="company">{metadata.nome_contratada}</span>
                          )}
                        </div>
                        <div className="actions">
                          <DeleteBtn onClick={() => handleDeleteIndividual(item.file_id, item.file_name)} disabled={actionLoading}>
                            <FontAwesomeIcon icon={faTrash} />
                          </DeleteBtn>
                        </div>
                      </div>

                      <div className="details-grid">
                        <div className="detail-item">
                          <span className="label">
                            <FontAwesomeIcon icon={faCalendarAlt} /> Início
                          </span>
                          <span className="value">{formatDate(metadata.data_inicio)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">
                            <FontAwesomeIcon icon={faCalendarAlt} /> Fim
                          </span>
                          <span className="value">{formatDate(metadata.data_fim)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">
                            <FontAwesomeIcon icon={faMoneyBillWave} /> Valor
                          </span>
                          <span className="value">{formatCurrency(metadata.valor)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">
                            <FontAwesomeIcon icon={faInfoCircle} /> CNPJ
                          </span>
                          <span className="value">{metadata.cnpj || '-'}</span>
                        </div>

                        <div className="detail-item full-width">
                          <span className="label">
                            <FontAwesomeIcon icon={faExclamationTriangle} /> Multa
                          </span>
                          <span className="value">{metadata.multa || '-'}</span>
                        </div>

                        <div className="detail-item full-width">
                          <span className="label">
                            <FontAwesomeIcon icon={faHeadset} /> Suporte
                          </span>
                          <span className="value">{metadata.contato_suporte || '-'}</span>
                        </div>
                      </div>

                      {metadata.resumo_50 && metadata.resumo_50 !== '-' && (
                        <div className="summary">
                          {metadata.resumo_50}
                        </div>
                      )}
                    </ContractBody>
                  </ContractContainer>
                );
              })
            )}
          </div>
        )}
      </MainCard>
    </PageWrapper>
  );
};

export default ContractsViewer;